import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, Pause, Play, Square, Loader2, AlertTriangle, ShieldCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtClock } from "@/lib/constants";
import { useRecorder } from "@/hooks/useRecorder";
import { useChunkUploader } from "@/hooks/useChunkUploader";
import { Waveform } from "@/components/record/Waveform";
import { ConsentDialog } from "@/components/record/ConsentDialog";
import { CourseMetaForm } from "@/components/record/CourseMetaForm";

const LiveTranscript = ({ segments, recording }) => {
  const ref = useRef(null);
  useEffect(() => { ref.current?.scrollTo({ top: ref.current.scrollHeight, behavior: "smooth" }); }, [segments]);
  const processing = segments.some((s) => s.status !== "done" && s.status !== "error");
  return (
    <div className="card-flat flex h-full min-h-[320px] flex-col p-5" data-testid="live-transcript">
      <div className="flex items-center justify-between">
        <p className="eyebrow">Transcription en direct</p>
        {processing && <span className="inline-flex items-center gap-1.5 text-xs text-primary"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Transcription…</span>}
      </div>
      <div ref={ref} className="mt-4 flex-1 space-y-3 overflow-y-auto pr-1 text-sm leading-relaxed" style={{ maxHeight: 420 }}>
        {segments.length === 0 && (
          <p className="text-muted-foreground">{recording ? "Le texte apparaît ici toutes les 30 secondes environ…" : "Démarre l'enregistrement pour voir la transcription s'afficher au fil du cours."}</p>
        )}
        {segments.map((s) => (
          <p key={s.index} data-testid={`transcript-segment-${s.index}`} className={s.status === "error" ? "text-destructive" : ""}>
            {s.status === "done" ? (s.text || <span className="italic text-muted-foreground">(silence)</span>)
              : s.status === "error" ? `Segment ${s.index + 1} non transcrit${s.detail ? ` — ${s.detail}` : ""}`
              : <span className="text-muted-foreground">Segment {s.index + 1} en cours de transcription…</span>}
          </p>
        ))}
      </div>
    </div>
  );
};

export default function Record() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("Autre");
  const [consentOpen, setConsentOpen] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [quotaMsg, setQuotaMsg] = useState(null);
  const recorderRef = useRef(null);
  const up = useChunkUploader({ onQuota: (m) => { recorderRef.current?.stop(); setQuotaMsg(m); toast.error(m); } });
  const rec = useRecorder({ onSegment: up.enqueue });
  recorderRef.current = rec;
  const active = rec.state === "recording" || rec.state === "paused";
  const unlimited = user?.usage?.limit_minutes == null;
  const remaining = Math.max(0, (user?.usage?.limit_minutes || 0) - (user?.usage?.used_minutes || 0));

  useEffect(() => {
    if (rec.state !== "recording") { document.title = "NoteMyClass AI"; return; }
    document.title = `● Enregistrement ${fmtClock(rec.elapsed)} — NoteMyClass AI`;
  }, [rec.state, rec.elapsed]);

  useEffect(() => {
    const warn = (e) => { if (active || up.pending()) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [active, up]);

  const begin = async () => {
    setConsentOpen(false);
    try {
      const { data } = await api.post("/courses", { title, subject, consent: true });
      up.courseId.current = data.course_id;
      if (await rec.start()) toast.success("Enregistrement démarré. Le micro est actif.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const onMain = () => {
    if (rec.state === "idle") return setConsentOpen(true);
    if (rec.state === "recording") return rec.pause();
    if (rec.state === "paused") return rec.resume();
  };

  const finish = async () => {
    setFinishing(true);
    rec.stop();
    await up.drain();
    if (up.failedCount()) {
      setFinishing(false);
      toast.error("Certains segments n'ont pas pu être transcrits. Réessaie avant de terminer, ou termine sans eux.");
      return;
    }
    await submitFinish();
  };

  const submitFinish = async () => {
    setFinishing(true);
    try {
      await api.post(`/courses/${up.courseId.current}/finish`);
      refresh();
      toast.success("Enregistrement terminé ! L'IA prépare tes notes.");
      navigate(`/app/cours/${up.courseId.current}`);
    } catch (e) {
      toast.error(errMsg(e));
      setFinishing(false);
    }
  };

  if (!rec.supported) {
    return <div className="card-flat p-8" data-testid="recorder-unsupported">Ton navigateur ne permet pas l'enregistrement audio. Utilise une version récente de Chrome, Edge, Firefox ou Safari, ou importe un fichier audio.</div>;
  }

  return (
    <div data-testid="record-page">
      <p className="eyebrow">Enregistrement intelligent</p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">Enregistrer un cours</h1>
      <p className="mt-2 text-sm text-muted-foreground">{unlimited ? <>Transcription <b className="text-foreground" data-testid="remaining-minutes">illimitée</b> avec ton offre Pro.</> : <>Il te reste <b className="text-foreground" data-testid="remaining-minutes">{Math.round(remaining)} minutes</b> de transcription ce mois-ci.</>}</p>

      <div className="mt-8"><CourseMetaForm title={title} setTitle={setTitle} subject={subject} setSubject={setSubject} disabled={active || rec.state === "stopped"} /></div>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.1fr_1fr]">
        <div className="card-flat relative overflow-hidden p-6 sm:p-10">
          <div className="glow left-1/2 top-1/3 h-56 w-56 -translate-x-1/2 bg-blue-500/20" />
          <div className="relative flex flex-col items-center">
            <div className="h-7">
              {rec.state === "recording" && <span data-testid="mic-active-indicator" className="inline-flex items-center gap-2 rounded-full bg-red-500/10 px-3 py-1 text-xs font-bold text-red-600 dark:text-red-400"><span className="h-2 w-2 animate-pulse rounded-full bg-red-500" /> MICRO ACTIF — ENREGISTREMENT EN COURS</span>}
              {rec.state === "paused" && <span data-testid="mic-paused-indicator" className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-3 py-1 text-xs font-bold text-amber-600">EN PAUSE — MICRO COUPÉ</span>}
            </div>
            <p data-testid="recording-timer" className="mt-4 font-mono text-5xl font-medium tabular-nums tracking-tight sm:text-6xl">{fmtClock(rec.elapsed)}</p>
            <div className="relative my-10">
              {rec.state === "recording" && <><span className="pulse-ring absolute inset-0 rounded-full bg-red-500/40" /><span className="pulse-ring absolute inset-0 rounded-full bg-red-500/30" style={{ animationDelay: ".6s" }} /></>}
              <button data-testid="mic-main-button" onClick={onMain} disabled={rec.state === "stopped" || finishing}
                aria-label="Microphone"
                className={`relative inline-flex h-32 w-32 items-center justify-center rounded-full text-white shadow-2xl transition-transform duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 sm:h-36 sm:w-36 ${rec.state === "recording" ? "bg-red-500" : "bg-brand"}`}>
                {rec.state === "recording" ? <Pause className="h-12 w-12" /> : <Mic className="h-12 w-12" />}
              </button>
            </div>
            <Waveform analyserRef={rec.analyserRef} active={rec.state === "recording"} />
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {rec.state === "idle" && (
                <button data-testid="start-recording-button" onClick={() => setConsentOpen(true)} className="bg-brand inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white">
                  <Mic className="h-4 w-4" /> Démarrer l'enregistrement
                </button>
              )}
              {active && (
                <>
                  <button data-testid="pause-recording-button" onClick={onMain} className="inline-flex items-center gap-2 rounded-full border bg-card px-6 py-3 text-sm font-semibold hover:border-primary/40">
                    {rec.state === "recording" ? <><Pause className="h-4 w-4" /> Pause</> : <><Play className="h-4 w-4" /> Reprendre</>}
                  </button>
                  <button data-testid="finish-recording-button" onClick={finish} disabled={finishing} className="inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm font-semibold text-background disabled:opacity-60">
                    {finishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />} Terminer
                  </button>
                </>
              )}
              {rec.state === "stopped" && !finishing && up.courseId.current && (
                <>
                  {up.failed.length > 0 && <button data-testid="retry-failed-segments-button" onClick={up.retryFailed} className="inline-flex items-center gap-2 rounded-full border bg-card px-5 py-3 text-sm font-semibold"><RotateCcw className="h-4 w-4" /> Réessayer les segments</button>}
                  <button data-testid="submit-finish-button" onClick={submitFinish} className="bg-brand rounded-full px-5 py-3 text-sm font-semibold text-white">Générer mes notes</button>
                </>
              )}
            </div>
            {(rec.error || quotaMsg) && (
              <p data-testid="recorder-error" className="mt-6 flex max-w-md gap-2 rounded-2xl bg-destructive/10 p-3.5 text-sm text-destructive"><AlertTriangle className="h-4 w-4 shrink-0" /> {quotaMsg || rec.error}</p>
            )}
          </div>
        </div>
        <LiveTranscript segments={up.segments} recording={rec.state === "recording"} />
      </div>
      <p className="mt-5 flex gap-2 text-xs text-muted-foreground"><ShieldCheck className="h-4 w-4 shrink-0 text-primary" /> L'enregistrement s'arrête si tu fermes la page. L'audio est envoyé de façon chiffrée par segments de 30 s, transcrit puis supprimé : en cas de coupure, rien de ce qui a déjà été transcrit n'est perdu.</p>
      <ConsentDialog open={consentOpen} onOpenChange={setConsentOpen} onConfirm={begin} />
    </div>
  );
}
